const EMAIL_ATTRIBUTION_COOKIE = '__ap_email_attribution';

/**
 * How long an email click stays associated with the visitor.
 *
 * Matched to the ad-attribution window (`adProfessionalCookie.ts` in the tracker
 * package): a reader who opens their Get Matched recommendations, browses over a
 * few days, and then enquires is exactly the journey the attribution exists to
 * see, so a tab-scoped store would lose most of it.
 */
const EMAIL_ATTRIBUTION_TTL_SECONDS = 7 * 24 * 60 * 60;

/**
 * The email campaign that brought the visitor here.
 *
 * Held in a cookie rather than session storage so it survives the visitor
 * leaving and coming back within the attribution window, and survives the
 * logged-out enquiry flow's full-page reload.
 */
export interface EmailAttribution {
    /** Series key stamped into tracking events, e.g. `GetMatchedResponse`. */
    source: string;
    /** The Get Matched submission the email answered, when the link carried it. */
    submissionId?: number;
}

/**
 * Reads the email attribution, or null when the visitor did not arrive from a
 * recognised email link inside the window.
 */
export function readEmailAttribution(): EmailAttribution | null {
    if (typeof document === 'undefined') {
        return null;
    }

    const match = document.cookie
        .split('; ')
        .find((entry) => entry.startsWith(`${EMAIL_ATTRIBUTION_COOKIE}=`));
    if (!match) {
        return null;
    }

    try {
        return parseEmailAttribution(
            JSON.parse(
                decodeURIComponent(
                    match.slice(EMAIL_ATTRIBUTION_COOKIE.length + 1),
                ),
            ),
        );
    } catch {
        // A malformed cookie means no attribution, not a broken enquiry.
        return null;
    }
}

/** Records the email campaign that brought the visitor here. */
export function writeEmailAttribution(value: EmailAttribution): void {
    if (typeof document === 'undefined') {
        return;
    }

    const encoded = encodeURIComponent(JSON.stringify(value));
    const attributes = [
        `${EMAIL_ATTRIBUTION_COOKIE}=${encoded}`,
        'path=/',
        `max-age=${EMAIL_ATTRIBUTION_TTL_SECONDS}`,
        'SameSite=Lax',
    ];

    if (window.location.protocol === 'https:') {
        attributes.push('Secure');
    }

    document.cookie = attributes.join('; ');
}

function parseEmailAttribution(value: unknown): EmailAttribution | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }

    const record = value as Record<string, unknown>;
    const source = record['source'];
    if (typeof source !== 'string' || source === '') {
        return null;
    }

    const submissionId = Number(record['submissionId']);

    return {
        source,
        ...(Number.isInteger(submissionId) &&
            submissionId > 0 && { submissionId }),
    };
}
